import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { Checkbox } from '@/components/elements/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Textarea } from '@/components/elements/textarea'
import { Modal } from '@/components/Modal'
import {
  createExternalCertificationCategory,
  updateExternalCertificationCategory,
} from '@/services/external_certifications.service'
import { toast } from '@/utils/helpers'

const AddEditCategoryModal = ({ entityId, editCategory, resetAndClose }) => {
  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
    },
  })

  const resetForm = () => {
    form.reset({
      name: '',
      description: '',
      isActive: true,
    })
    resetAndClose()
  }

  useEffect(() => {
    if (editCategory) {
      form.reset({
        name: editCategory.name || '',
        description: editCategory.description || '',
        isActive: editCategory.isActive ?? true,
      })
    }
  }, [editCategory, form])

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()

  const { isPending, mutate: mutateCategory } = useMutation({
    mutationFn: (data) => {
      const payload = {
        ...data,
        categoryType: 'Entity',
        entity: entityId,
      }
      return editCategory
        ? updateExternalCertificationCategory(entityId, editCategory.id, payload)
        : createExternalCertificationCategory(entityId, payload)
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} ${editCategory ? 'updated' : 'added'} successfully`)
      resetForm()
      queryClient.invalidateQueries({ queryKey: ['external-certification-categories', entityId] })
    },
  })

  const onSubmit = async (data) => mutateCategory(data)

  return (
    <Modal
      actions={[
        {
          label: 'Cancel',
          onClick: () => resetAndClose(),
          type: 'cancel',
        },
        {
          label: `${editCategory ? 'Update' : 'Add'} Category`,
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input id="name" placeholder="e.g. CPR Certification" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'Name is required' }}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      id="description"
                      placeholder="Describe this credential category"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-y-0 space-x-3 rounded-md border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Inactive categories will not be available for new credential submissions.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </form>
        </Form>
      }
      loading={isPending}
      title={`${editCategory ? 'Update' : 'Add'} Credential Category`}
      setOpen={resetForm}
    />
  )
}

AddEditCategoryModal.propTypes = {
  entityId: PropTypes.string.isRequired,
  editCategory: PropTypes.object,
  resetAndClose: PropTypes.func.isRequired,
}

export default AddEditCategoryModal
